import { Badge, Tooltip } from "@chakra-ui/react";
import { FunctionComponent, ReactElement } from "react";

import usePermission from "../../hooks/usePermission";

interface AttorneyActiveBadgeProps {
	/** Whether the attorney is active in the Claims System (a signed-up Provider account). */
	active?: boolean;
	/**
	 * When true, also render a gray "Inactive" badge for attorneys who are not active (e.g. the
	 * Payments table, which surfaces both states). Defaults to false — most surfaces only flag
	 * "Active" and render nothing otherwise.
	 */
	showInactive?: boolean;
}

/**
 * "Active" badge shown next to an attorney who has signed up for claims (green), and optionally
 * an "Inactive" badge (gray) when `showInactive` is set.
 *
 * Drop this in anywhere an attorney is displayed — it renders nothing for Provider users. The
 * "internal users only, not Providers" rule lives here so individual call sites can't forget it,
 * keeping the badge consistent everywhere.
 */
const AttorneyActiveBadge: FunctionComponent<AttorneyActiveBadgeProps> = ({
	active,
	showInactive = false
}: AttorneyActiveBadgeProps): ReactElement => {
	const { isProvider } = usePermission();

	// Internal-only: never surface attorney claims-status to Provider users.
	if (isProvider) {
		return <></>;
	}

	if (active) {
		return (
			<Tooltip label="This attorney is signed up for claims">
				<Badge colorScheme="green" fontSize="xs" ml="1" style={{ verticalAlign: "initial" }}>
					Active
				</Badge>
			</Tooltip>
		);
	}

	if (showInactive) {
		return (
			<Tooltip label="This attorney is not signed up for claims">
				<Badge colorScheme="gray" fontSize="xs" ml="1" style={{ verticalAlign: "initial" }}>
					Inactive
				</Badge>
			</Tooltip>
		);
	}

	return <></>;
};

export default AttorneyActiveBadge;
